import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { appEvents } from "@/shared/lib/events";
import { useAgentChatStore } from "@/features/companion";
import { MarcusAutopilotOverlay } from "./MarcusAutopilotOverlay";
import { MarcusOnboardingIntroOverlay } from "./MarcusOnboardingIntroOverlay";
import {
  MARCUS_ONBOARDING_NEXT_TARGET as NEXT_TARGET,
  MarcusOnboardingSpotlight,
} from "./MarcusOnboardingSpotlight";
import { enqueueCursorMove } from "../../services/marcusCursorQueue";
import {
  CONTINUE_AGENTS_LIST_PROMPT,
  CONTINUE_META_PROMPT,
  CONTINUE_PROMPT,
  CONTINUE_VIDEO_PROMPT,
  EXPLORE_PATH,
  inferOnboardingStageFromPath,
  type OnboardingStage,
  routeWithWorkspace,
  type RunPhase,
  START_PROMPT,
  STATIC_ADCLONE_PATH,
  STATIC_ADCLONE_TARGET_TIMEOUT_MS,
  STATIC_ADS_PATH,
  waitForHighlightTarget,
  WORKFLOW_SLUG,
} from "./marcusOnboardingFlow";
import {
  ONBOARDING_HIGHLIGHT_LABELS,
  ONBOARDING_QUICK_STATIC_ADCLONE_COPY,
  STATIC_ADS_CLONE_TARGET,
} from "./marcusOnboardingTargets";
import { useMarcusOnboardingArrivalHandler } from "./useMarcusOnboardingArrivalHandler";
import { useMarcusOnboardingVoice } from "./useMarcusOnboardingVoice";
import { useMarcusOnboardingPreface } from "./useMarcusOnboardingPreface";
import {
  getGateStage,
  getOnboardingNextStep,
  ONBOARDING_STAGE_LABELS,
} from "./marcusOnboardingGate";
import {
  clearPersistedTimeline,
  publishOnboardingPrompt,
  readMarcusOnboardingState,
  writeMarcusOnboardingState,
  type MarcusOnboardingState,
} from "./marcusOnboardingState";

export { readMarcusOnboardingState, type MarcusOnboardingState } from "./marcusOnboardingState";

export interface MarcusOnboardingControllerProps {
  userId: string | null;
  pathname: string;
  connectedWorkflowSlug: string | null;
  connectedSessionId: string | null;
}

export function MarcusOnboardingController({
  userId,
  pathname,
  connectedWorkflowSlug,
  connectedSessionId,
}: MarcusOnboardingControllerProps) {
  const navigate = useNavigate();
  const [storedState, setStoredState] = useState<MarcusOnboardingState | null>(() =>
    readMarcusOnboardingState(userId),
  );
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupPromptLabel, setPopupPromptLabel] = useState("Start guided tour");
  const [popupPrimaryActionLabel, setPopupPrimaryActionLabel] = useState("Start tour");
  const [popupPersistsDismissal, setPopupPersistsDismissal] = useState(true);
  const [phase, setPhase] = useState<RunPhase>("idle");
  const [activeStage, setActiveStage] = useState<OnboardingStage | null>(null);
  const [spotlightTarget, setSpotlightTarget] = useState<string | null>(null);
  const [companionDockUnlocked, setCompanionDockUnlocked] = useState(false);
  const clearSpotlightTimerRef = useRef<number | null>(null);
  const nextGateTimerRef = useRef<number | null>(null);
  const onboardingPromptRunRef = useRef(0);
  const cursorStepRunRef = useRef(0);
  const {
    avatarRef,
    clearVoiceIntroStartWait,
    clearVoiceIntroTimer,
    liveAvatarConnected,
    liveAvatarSpeaking,
    prepareVoiceForOnboardingStep,
    resolveVoiceIntroStartWait,
    scheduleVoiceIntroStop,
    setLiveAvatarConnected,
    setLiveAvatarSpeaking,
    speakVoiceIntro,
    speakOnboardingHighlightAndWait,
    stopVoiceIntro,
    voiceIntroActive,
    waitForVoiceIntroStart,
  } = useMarcusOnboardingVoice();
  const { cancelPreface, requestPreface } = useMarcusOnboardingPreface({
    prepareVoiceForOnboardingStep,
    speakVoiceIntro,
    waitForVoiceIntroStart,
  });

  const isMarcusConnected = connectedWorkflowSlug === WORKFLOW_SLUG && Boolean(connectedSessionId);
  const isEligible = Boolean(userId) && pathname === EXPLORE_PATH && isMarcusConnected;

  useEffect(() => {
    setStoredState(readMarcusOnboardingState(userId));
    setPopupOpen(false);
    setPopupPromptLabel("Start guided tour");
    setPopupPrimaryActionLabel("Start tour");
    setPopupPersistsDismissal(true);
    setPhase("idle");
    setActiveStage(null);
    setSpotlightTarget(null);
    setCompanionDockUnlocked(false);
    useAgentChatStore.getState().setMarcusOnboardingDockLocked(false);
    stopVoiceIntro();
  }, [stopVoiceIntro, userId]);

  useEffect(() => {
    return () => {
      clearVoiceIntroTimer();
      clearVoiceIntroStartWait();
    };
  }, [clearVoiceIntroStartWait, clearVoiceIntroTimer]);

  useEffect(() => {
    if (isEligible && storedState === null) {
      setPopupPromptLabel("Start guided tour");
      setPopupPrimaryActionLabel("Start tour");
      setPopupPersistsDismissal(true);
      setPopupOpen(true);
    } else {
      setPopupOpen(false);
    }
  }, [isEligible, storedState]);

  const dismiss = useCallback(() => {
    if (!userId) return;
    stopVoiceIntro();
    appEvents.publish({ type: "marcus-onboarding:agent-list-showcase", active: false });
    setCompanionDockUnlocked(false);
    useAgentChatStore.getState().setMarcusOnboardingDockLocked(false);
    if (popupPersistsDismissal) {
      writeMarcusOnboardingState(userId, "dismissed");
      setStoredState("dismissed");
    }
    onboardingPromptRunRef.current += 1;
    cancelPreface();
    if (nextGateTimerRef.current) {
      window.clearTimeout(nextGateTimerRef.current);
      nextGateTimerRef.current = null;
    }
    if (clearSpotlightTimerRef.current) {
      window.clearTimeout(clearSpotlightTimerRef.current);
      clearSpotlightTimerRef.current = null;
    }
    setPopupOpen(false);
    setPhase("idle");
    setActiveStage(null);
    setSpotlightTarget(null);
  }, [cancelPreface, popupPersistsDismissal, stopVoiceIntro, userId]);

  const startOnboarding = useCallback(
    (label = "Start guided tour", options: { speakIntro?: boolean } = {}) => {
      if (!userId) return;
      clearPersistedTimeline();
      appEvents.publish({ type: "marcus-onboarding:agent-list-showcase", active: false });
      setCompanionDockUnlocked(false);
      writeMarcusOnboardingState(userId, "accepted");
      setStoredState("accepted");
      setPopupOpen(false);
      setPhase("explore");
      setActiveStage("explore");
      setSpotlightTarget(null);
      if (nextGateTimerRef.current) {
        window.clearTimeout(nextGateTimerRef.current);
      }
      const chat = useAgentChatStore.getState();
      chat.setMarcusOnboardingDockLocked(true);
      const promptRunId = ++onboardingPromptRunRef.current;
      const publishStartPrompt = () => {
        if (onboardingPromptRunRef.current !== promptRunId) return;
        publishOnboardingPrompt(START_PROMPT, label);
      };
      if (options.speakIntro) requestPreface(promptRunId, publishStartPrompt);
      else publishStartPrompt();
    },
    [requestPreface, userId],
  );

  const accept = useCallback(() => {
    startOnboarding(popupPromptLabel, { speakIntro: true });
  }, [popupPromptLabel, startOnboarding]);

  const showReplayDialog = useCallback(() => {
    if (!isEligible) return;
    stopVoiceIntro();
    setPopupPromptLabel("Replay interactive onboarding");
    setPopupPrimaryActionLabel("Replay tour");
    setPopupPersistsDismissal(false);
    setPopupOpen(true);
  }, [isEligible, stopVoiceIntro]);

  useEffect(() => {
    return appEvents.subscribe("marcus-onboarding:restart", showReplayDialog);
  }, [showReplayDialog]);

  useEffect(() => {
    return appEvents.subscribe("marcus-onboarding:start-direct", (event) => {
      startOnboarding(event.label ?? "Start guided tour", {
        speakIntro: event.speakIntro ?? true,
      });
    });
  }, [startOnboarding]);

  useEffect(() => {
    useAgentChatStore
      .getState()
      .setMarcusOnboardingDockLocked(phase !== "idle" && !companionDockUnlocked);
  }, [companionDockUnlocked, phase]);

  useEffect(() => {
    return () => {
      useAgentChatStore.getState().setMarcusOnboardingDockLocked(false);
    };
  }, []);

  const complete = useCallback(() => {
    if (!userId) return;
    stopVoiceIntro();
    appEvents.publish({ type: "marcus-onboarding:agent-list-showcase", active: false });
    setCompanionDockUnlocked(false);
    useAgentChatStore.getState().setMarcusOnboardingDockLocked(false);
    writeMarcusOnboardingState(userId, "completed");
    setStoredState("completed");
    setPhase("idle");
    setActiveStage(null);
    setSpotlightTarget(null);
    cancelPreface();
    if (nextGateTimerRef.current) {
      window.clearTimeout(nextGateTimerRef.current);
    }
  }, [cancelPreface, stopVoiceIntro, userId]);

  useMarcusOnboardingArrivalHandler({
    phase,
    activeStage,
    navigate,
    speakOnboardingHighlightAndWait,
    cursorStepRunRef,
    clearSpotlightTimerRef,
    nextGateTimerRef,
    setPhase,
    setSpotlightTarget,
    setCompanionDockUnlocked,
  });

  const continueOnboarding = useCallback(() => {
    const continueToStage = (stage: OnboardingStage, prompt: string, label: string) => {
      setPhase("continuing");
      setActiveStage(stage);
      setSpotlightTarget(null);
      if (nextGateTimerRef.current) {
        window.clearTimeout(nextGateTimerRef.current);
      }
      prepareVoiceForOnboardingStep();
      appEvents.publish({ type: "marcus-plan-advance", target: NEXT_TARGET });
      publishOnboardingPrompt(prompt, label);
    };

    const continueToStaticAdclone = () => {
      setPhase("continuing");
      setActiveStage("static_adclone");
      setSpotlightTarget(null);
      if (nextGateTimerRef.current) {
        window.clearTimeout(nextGateTimerRef.current);
      }
      prepareVoiceForOnboardingStep();
      appEvents.publish({ type: "marcus-plan-advance", target: NEXT_TARGET });
      navigate(routeWithWorkspace(STATIC_ADS_PATH));
      void waitForHighlightTarget(STATIC_ADS_CLONE_TARGET, STATIC_ADCLONE_TARGET_TIMEOUT_MS).then(
        (found) => {
          if (!found) {
            navigate(routeWithWorkspace(STATIC_ADCLONE_PATH));
            return;
          }
          enqueueCursorMove({
            target: STATIC_ADS_CLONE_TARGET,
            comment: ONBOARDING_QUICK_STATIC_ADCLONE_COPY,
            pulses: 5,
            source: "onboarding",
          });
        },
      );
    };

    const stageForContinue =
      activeStage === "agents_list" || activeStage === "static_adclone"
        ? activeStage
        : (inferOnboardingStageFromPath(pathname) ?? activeStage);

    if (stageForContinue === "explore") {
      continueToStage("image_ads", CONTINUE_PROMPT, "Continue onboarding to Image Ads");
    } else if (stageForContinue === "image_ads") {
      continueToStage("video_ads", CONTINUE_VIDEO_PROMPT, "Continue onboarding to Video Ads");
    } else if (stageForContinue === "video_ads") {
      continueToStage(
        "meta_campaigns",
        CONTINUE_META_PROMPT,
        "Continue onboarding to Meta Campaigns",
      );
    } else if (stageForContinue === "meta_campaigns") {
      continueToStage(
        "agents_list",
        CONTINUE_AGENTS_LIST_PROMPT,
        "Continue onboarding to Agents List",
      );
    } else if (stageForContinue === "agents_list") {
      continueToStaticAdclone();
    } else if (stageForContinue === "static_adclone") {
      complete();
    }
  }, [activeStage, complete, navigate, pathname, prepareVoiceForOnboardingStep]);

  const showNextButton = phase === "waiting_for_next";
  const nextStep = showNextButton
    ? getOnboardingNextStep(getGateStage(activeStage, pathname))
    : null;
  const activeTarget = showNextButton ? NEXT_TARGET : spotlightTarget;
  const compactHighlightLabel =
    !showNextButton && spotlightTarget
      ? ONBOARDING_HIGHLIGHT_LABELS[spotlightTarget]
      : activeStage
        ? ONBOARDING_STAGE_LABELS[activeStage]
        : null;

  const [nextCountdown, setNextCountdown] = useState(6);

  useEffect(() => {
    if (!showNextButton) {
      setNextCountdown(6);
      return;
    }
    if (nextCountdown <= 0) {
      continueOnboarding();
      return;
    }
    const timer = window.setTimeout(() => setNextCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [showNextButton, nextCountdown, continueOnboarding]);

  return (
    <>
      {phase !== "idle" && pathname.startsWith(EXPLORE_PATH) && (
        <MarcusAutopilotOverlay showTimeline={false} />
      )}

      {phase !== "idle" && (
        <MarcusOnboardingSpotlight target={activeTarget} showNextButton={showNextButton} />
      )}

      {(popupOpen || phase !== "idle" || voiceIntroActive) && (
        <MarcusOnboardingIntroOverlay
          open={popupOpen}
          voiceIntroActive={voiceIntroActive}
          liveAvatarConnected={liveAvatarConnected}
          liveAvatarSpeaking={liveAvatarSpeaking}
          highlightLabel={compactHighlightLabel}
          avatarRef={avatarRef}
          primaryActionLabel={popupPrimaryActionLabel}
          showNextButton={showNextButton}
          nextStep={nextStep}
          countdown={nextCountdown}
          onNext={continueOnboarding}
          onAccept={accept}
          onDismiss={dismiss}
          onAvatarConnected={() => setLiveAvatarConnected(true)}
          onAvatarDisconnected={() => {
            setLiveAvatarConnected(false);
            setLiveAvatarSpeaking(false);
            resolveVoiceIntroStartWait();
            if (voiceIntroActive && phase === "idle") scheduleVoiceIntroStop(1200);
          }}
          onAvatarStartSpeaking={() => {
            setLiveAvatarSpeaking(true);
            resolveVoiceIntroStartWait();
            clearVoiceIntroTimer();
          }}
          onAvatarStopSpeaking={() => {
            setLiveAvatarSpeaking(false);
            if (phase === "idle") scheduleVoiceIntroStop(1200);
          }}
        />
      )}
    </>
  );
}

export default MarcusOnboardingController;
